import { useCallback, useEffect, useState } from 'react';

import { useTracking } from 'hooks/analytic-tracking';
import { useReportingTrends } from 'hooks/use-reporting-trends';
import { Badge } from 'react-bootstrap';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { Table } from 'components/Table/Table';

import { selectCompanyChecks } from 'api/company-checks-slice';
import {
  useGetRefereesFiltersQuery,
  useLazyDownloadRefereesReportQuery,
  useLazyGetRefereesReportingQuery,
  useLazyGetRefereesStatsQuery,
  useLazyGetRefereesTrendsQuery,
} from 'api/reporting-api';
import { selectUser } from 'api/user-slice';

import { ReportingChart } from '../../components/ReportingChart/ReportingChart';
import { ReportingWidget } from '../../components/ReportingWidget/ReportingWidget';

const REFEREES_SERIES = [
  { key: 'completed', label: 'Completed', color: '#039d41' },
  { key: 'in_progress', label: 'In-Progress', color: '#2f80ed' },
  { key: 'overdue', label: 'Overdue', color: '#ef4444' },
  { key: 'canceled', label: 'Cancelled', color: '#5c4d78' }, // $neutral-06
];

export const RefereesReporting = ({ locale, setLoading = () => {} }) => {
  const [getRefereesReporting] = useLazyGetRefereesReportingQuery();
  const [getRefereesStats] = useLazyGetRefereesStatsQuery();
  const [getRefereesTrends] = useLazyGetRefereesTrendsQuery();
  const [downloadRefereesReport] = useLazyDownloadRefereesReportQuery();
  const { data: filters } = useGetRefereesFiltersQuery();

  const user = useSelector(selectUser);
  const companyChecks = useSelector(selectCompanyChecks);

  const [hasDefaultReference, setHasDefaultReference] = useState(false);
  const [hasCustomReference, setHasCustomReference] = useState(false);

  const [referees, setReferees] = useState([]);
  const [stats, setStats] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const trendsFetcher = useCallback(
    async params => getRefereesTrends(params).unwrap(),
    [getRefereesTrends],
  );
  const {
    trends,
    groupBy,
    datePreset,
    datePresets,
    fetchTrends,
    handleGroupByChange,
    handleDatePresetChange,
  } = useReportingTrends(trendsFetcher);

  const fetchReferees = async (params = {}) => {
    setLoading(true);
    setQueryParams(params);
    const result = await getRefereesReporting(params).unwrap();
    const stats = await getRefereesStats(params).unwrap();
    setStats(stats);
    setPagination(result.pagination);
    setReferees(result.referees);
    try {
      await fetchTrends(params);
    } catch {
      // Trends failed; table and stats are already loaded, don't block UI
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReferees();

    setHasDefaultReference(companyChecks.some(cc => cc.type === 'reference'));
    setHasCustomReference(companyChecks.some(cc => cc.type === 'custom_reference'));
  }, []);

  const download = async () => {
    setLoading(true);
    await downloadRefereesReport({ queryParams }).unwrap();
    setLoading(false);
  };

  const filteredSeries = queryParams.status?.length
    ? REFEREES_SERIES.filter(s => queryParams.status.includes(s.key.replace(/_/g, '-')))
    : REFEREES_SERIES;

  return (
    <Table.Context onQueryUpdate={fetchReferees} defaultQuery={{ page: 1 }}>
      <Table.FloatingFilters className="u-margin-bottom--large surface-white">
        <Table.FloatingFiltersLeft>
          <Table.DateRangeFilter title="Completed within" filterName="completed_at" />
          <Table.MultiSelectFilter
            title="Status"
            filterName="status"
            type="status"
            useDefault={false}
            options={[
              { name: 'In-Progress', value: 'in-progress' },
              { name: 'Completed', value: 'completed' },
              { name: 'Overdue', value: 'overdue' },
              { name: 'Canceled', value: 'canceled' },
            ]}
          />
          <Table.MultiSelectFilter
            title="Engagement"
            filterName="extra_question"
            type="status"
            useDefault={false}
            options={[
              { name: 'Engaged', value: 'Yes' },
              { name: 'Not Engaged', value: 'No' },
            ]}
          />
          <Table.MultiSelectFilter
            title="Suspicious"
            filterName="warning_flag"
            type="status"
            useDefault={false}
            options={[
              { name: 'Suspicious', value: true },
              { name: 'Non Suspicious', value: false },
            ]}
          />
          {hasCustomReference && (
            <Table.MultiSelectFilter
              title="Forms"
              filterName="form_id"
              type="status"
              useDefault={false}
              options={filters?.custom_reference_forms}
            />
          )}
          {hasDefaultReference && (
            <Table.MultiSelectFilter
              title="Forms"
              filterName="typeform_id"
              type="status"
              useDefault={false}
              options={filters?.reference_forms}
            />
          )}
        </Table.FloatingFiltersLeft>
        <Table.FloatingFiltersRight>
          <Button icon="download" onClick={download}>
            Export
          </Button>
        </Table.FloatingFiltersRight>
      </Table.FloatingFilters>
      <ReportingWidget>
        <ReportingWidget.Tile>
          <ReportingWidget.Badge>Total Referees</ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.total_referees}</ReportingWidget.Value>
          <ReportingWidget.Label></ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile>
          <ReportingWidget.Badge>Average Completion</ReportingWidget.Badge>
          <ReportingWidget.Value>
            {stats?.avg_completion_time}
            <span className="title-3">h</span>
          </ReportingWidget.Value>
          <ReportingWidget.Label></ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Divider />
        <ReportingWidget.Tile
          type={ReportingWidget.Types.InProgress}
          disabled={queryParams.status && !queryParams.status.includes('in-progress')}>
          <ReportingWidget.Badge>
            In-Progress (
            {stats.total_referees == 0
              ? 0
              : Math.round((stats?.in_progress * 100) / stats.total_referees)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.in_progress}</ReportingWidget.Value>
          <ReportingWidget.Label>Referees</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Overdue}
          disabled={queryParams.status && !queryParams.status.includes('overdue')}>
          <ReportingWidget.Badge>
            Overdue (
            {stats.total_referees == 0
              ? 0
              : Math.round((stats?.overdue * 100) / stats.total_referees)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.overdue}</ReportingWidget.Value>
          <ReportingWidget.Label>Referees</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Completed}
          disabled={queryParams.status && !queryParams.status.includes('completed')}>
          <ReportingWidget.Badge>
            Complete (
            {stats.total_referees == 0
              ? 0
              : Math.round((stats?.completed * 100) / stats.total_referees)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.completed}</ReportingWidget.Value>
          <ReportingWidget.Label>Referees</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Warning}
          disabled={queryParams.status && !queryParams.status.includes('completed')}>
          <ReportingWidget.Badge>
            Suspicious (
            {stats.completed == 0 ? 0 : Math.round((stats?.suspicious * 100) / stats.completed)}%)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.suspicious}</ReportingWidget.Value>
          <ReportingWidget.Label>Referees</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Canceled}
          disabled={queryParams.status && !queryParams.status.includes('canceled')}>
          <ReportingWidget.Badge>
            Cancelled (
            {stats.total_referees == 0
              ? 0
              : Math.round((stats?.canceled * 100) / stats.total_referees)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.canceled}</ReportingWidget.Value>
          <ReportingWidget.Label>Referees</ReportingWidget.Label>
        </ReportingWidget.Tile>
      </ReportingWidget>
      <ReportingChart
        data={trends}
        series={filteredSeries}
        groupBy={groupBy}
        onGroupByChange={newGroupBy => handleGroupByChange(newGroupBy, queryParams)}
        datePreset={datePreset}
        datePresets={datePresets}
        onDatePresetChange={presetKey => handleDatePresetChange(presetKey, queryParams)}
      />
      <Table.TableContent>
        <Table.Filters>
          <Table.Search placeholder="Search by referee name..." />
        </Table.Filters>
        <Table className="u-margin--large">
          <Table.Head>
            <Table.Row>
              <Table.Header sortKey="referees.first_name">Name</Table.Header>
              <Table.Header sortKey="referees.status">Status</Table.Header>
              <Table.Header sortKey="referees.email">Email</Table.Header>
              <Table.Header sortKey="candidates.first_name">Candidate Name</Table.Header>
              <Table.Header sortKey="candidates.department">Candidate Department</Table.Header>
              {hasCustomReference && (
                <Table.Header sortKey="referees.submitted_at">Submitted</Table.Header>
              )}
              <Table.Header sortKey="referees.completed_at">Completed</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {referees.map(referee => (
              <Table.Row key={referee.id}>
                <Table.Col>
                  {referee.first_name} {referee.last_name}
                </Table.Col>
                <Table.Col>
                  <Badge className={`u-status-${referee.status}`}>{referee.status}</Badge>
                </Table.Col>
                <Table.Col>{referee.email}</Table.Col>
                <Table.Col>
                  <Link className="u-link dark" to={`/check/${referee.candidate_id}`}>
                    {referee.candidate_first_name} {referee.candidate_last_name}
                  </Link>
                </Table.Col>
                <Table.Col>{referee.department}</Table.Col>
                {hasCustomReference && (
                  <Table.Col>
                    {referee.submitted_at ? (
                      <Moment format={locale.dateSlashFormat}>{referee.submitted_at}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                )}
                <Table.Col>
                  {referee.completed_at ? (
                    <Moment format={locale.dateSlashFormat}>{referee.completed_at}</Moment>
                  ) : (
                    '-'
                  )}
                </Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Pagination {...pagination} />
        </Table>
      </Table.TableContent>
    </Table.Context>
  );
};
